import { ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { switchMap } from 'rxjs/operators';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { AdminResetDsConnectionService } from './admin-reset-ds-connection.service';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  generalConfigFromGlobal,
  mergeEnvToBaseUrls,
  resolveFilterParams,
  resolveGlobalParams,
  resolveRowPayload,
} from '../../../shared/workspace-element-context';
import { AdminScreenContextService } from '../../shared/admin-screen-context.service';

/**
 * Angular 12 migration of admin/adminResetDSConnection.
 * Replicates: dropdown (apis), refreshFromDb checkbox, tags list, Save (reTriggerMessage), Cancel.
 */
@Component({
    selector: 'gtw-admin-reset-ds-connection',
    templateUrl: './admin-reset-ds-connection.component.html',
    styleUrls: ['./admin-reset-ds-connection.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class AdminResetDsConnectionComponent implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);

  @Output('request-grid-update') requestGridUpdate = new EventEmitter<string>();

  private readonly subs = new Subscription();

  filterParams: any = {};
  baseURLs: any = {};
  /** Legacy name: global runtime params bag (not GlobalService). */
  globalService: any = {};
  GENERAL_CONFIG: any = {};
  USER_SETTINGS: any = {};
  rowData: any = {};
  private isInitialized = false;

  modalTitle = 'Reset Data source connection';
  apis: { name: string; value: string }[] = [
    { name: 'API', value: 'gtw' },
    { name: 'ADMIN', value: 'admin' },
    { name: 'COMMON-API', value: 'common' },
    { name: 'CUSTOM-API', value: 'custom' },
    { name: 'SCHEDULER-API', value: 'scheduler' },
    { name: 'EFILE-API', value: 'efile' },
    { name: 'BULK-PDF-ENGINE', value: 'bulkpdf' },
    { name: 'PDF-ENGINE', value: 'pdf' },
  ];
  selectedApi = 'gtw';
  refreshFromDb = false;
  includeCoreDS = true;
  tags: string[] = [];
  saveloader = false;

  constructor(
    private cdr: ChangeDetectorRef,
    private gtwSnackbarService: GtwSnackbarService,
    private adminResetDsConnectionService: AdminResetDsConnectionService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService,
    private adminScreenContext: AdminScreenContextService,
  ) {}

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subs.add(
      this.gtwWorkspace.setTabActive.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
    this.subs.add(
      this.gtwGlobal.globalParamsChanged$.subscribe(() => {
        this.isInitialized = false;
        this.applyWorkspaceContext();
        this.tryInit();
      })
    );
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    this.baseURLs = mergeEnvToBaseUrls(this.baseURLs, this.gtwGlobal.GENERAL_CONFIG);
    this.GENERAL_CONFIG = generalConfigFromGlobal(this.gtwGlobal);
    this.globalService = resolveGlobalParams(this.adminScreenContext, this.gtwGlobal, this.gtwWorkspace);
    this.USER_SETTINGS = this.gtwApp.USER_SETTINGS || {};
    this.rowData = resolveRowPayload(this.adminScreenContext, this.gtwWorkspace);
    this.filterParams = resolveFilterParams(this.adminScreenContext, this.gtwWorkspace);
    this.cdr.markForCheck();
  }

  private tryInit(): void {
    if (this.isInitialized) return;
    if (!this.GENERAL_CONFIG || Object.keys(this.GENERAL_CONFIG).length === 0) return;
    this.isInitialized = true;
    this.initTags();
    this.cdr.detectChanges();
  }

  private initTags(): void {
    this.tags = [];
    const gridData = this.rowData;
    if (gridData && Array.isArray(gridData)) {
      gridData.forEach((item: any) => {
        const d = item?.data ?? item;
        if (d?.CLIENT_DATASOURCE) this.tags.push(d.CLIENT_DATASOURCE);
      });
    } else if (gridData && typeof gridData === 'object' && gridData.CLIENT_DATASOURCE) {
      this.tags.push(gridData.CLIENT_DATASOURCE);
    }
  }

  onServiceChange(): void {
    this.cdr.detectChanges();
  }

  removeTag(index: number): void {
    this.tags.splice(index, 1);
    this.cdr.detectChanges();
  }

  reTriggerMessage(): void {
    if (!this.selectedApi) {
      this.gtwSnackbarService.openSnackBar('Please select the service to reset the connection.', 'gtw-snackbar--yellow');
      return;
    }
    this.saveloader = true;
    this.cdr.detectChanges();
    const client_key = this.USER_SETTINGS?.user?.client_key ?? this.filterParams?.client_key ?? '';
    const resetParams = {
      api: this.selectedApi,
      client_key,
      datasource: this.tags.toString(),
      refreshFromDB: this.refreshFromDb,
    };
    if (this.includeCoreDS) {
      // First purge core DS on the same API, then reset client datasources
      const purgeParams = {
        api: this.selectedApi,
        datasource: undefined as string | undefined,
        includeCoreDS: true,
      };
      this.adminResetDsConnectionService.purgeConnections(purgeParams, this.GENERAL_CONFIG).pipe(
        switchMap(() => this.adminResetDsConnectionService.resetDsConnection(resetParams, this.GENERAL_CONFIG)),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe({
        next: (res: any) => {
          this.saveloader = false;
          const data = res?.data ?? res;
          if (data?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Successfully purged core and reset client connections.', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(data?.message || 'Reset failed.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.saveloader = false;
          this.gtwSnackbarService.openSnackBar(err?.error?.message || err?.message || 'Error resetting connection.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
    } else {
      // Direct reset without purging core
      this.adminResetDsConnectionService.resetDsConnection(resetParams, this.GENERAL_CONFIG).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (res: any) => {
          this.saveloader = false;
          const data = res?.data ?? res;
          if (data?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Successfully reset the connection.', 'gtw-snackbar--green');
            this.requestGridUpdate.emit('refresh');
          } else {
            this.gtwSnackbarService.openSnackBar(data?.message || 'Reset failed.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.saveloader = false;
          this.gtwSnackbarService.openSnackBar(err?.error?.message || err?.message || 'Error resetting connection.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        },
      });
    }
  }

  cancel(): void {
    this.requestGridUpdate.emit('cancel');
  }

  purgeConnections(): void {
    if (!this.selectedApi) {
      this.gtwSnackbarService.openSnackBar('Please select the service to purge connections.', 'gtw-snackbar--yellow');
      return;
    }
    this.saveloader = true;
    this.cdr.detectChanges();
    const params = {
      api: this.selectedApi,
      datasource: this.tags.length > 0 ? this.tags.toString() : undefined,
      includeCoreDS: this.includeCoreDS,
    };
    this.adminResetDsConnectionService.purgeConnections(params, this.GENERAL_CONFIG).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res: any) => {
        this.saveloader = false;
        const data = res?.data ?? res;
        if (data?.callSuccess === '1') {
          this.gtwSnackbarService.openSnackBar(
            `Successfully purged pools: ${data.purgedPools || 'all'}`, 'gtw-snackbar--green'
          );
          this.requestGridUpdate.emit('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(data?.message || 'Purge failed.', 'gtw-snackbar--red');
        }
        this.cdr.detectChanges();
      },
      error: (err) => {
        this.saveloader = false;
        this.gtwSnackbarService.openSnackBar(err?.error?.message || err?.message || 'Error purging connections.', 'gtw-snackbar--red');
        this.cdr.detectChanges();
      },
    });
  }
}
